<template>
    <!-- Mobile-only full-screen tour overlay -->
    <Teleport to="body">
        <div
            v-if="mobileVisible && !finishing"
            class="fixed inset-0 z-[100000] sm:hidden flex flex-col items-stretch justify-start"
            aria-live="polite"
        >
            <div
                class="flex flex-col flex-1 w-full bg-white dark:bg-slate-800 overflow-y-auto self-stretch"
            >
                <!-- Navbar -->
                <div
                    class="flex w-full bg-slate-50 items-center gap-2 pt-5 pr-4 pb-1 pl-6"
                >
                    <div class="flex flex-col items-start gap-0 flex-1">
                        <span
                            class="text-[14px] leading-5 font-normal text-slate-500"
                        >
                            {{ mobileIndex + 1 }} of {{ mobileSteps.length }}
                        </span>
                    </div>
                    <button
                        type="button"
                        @click="mobileCancel"
                        class="flex p-2 rounded hover:bg-slate-100"
                        aria-label="Close"
                    >
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M3.57733 3.57806C3.90277 3.25263 4.4304 3.25263 4.75584 3.57806L9.99992 8.82214L15.244 3.57806C15.5694 3.25263 16.0971 3.25263 16.4225 3.57806C16.7479 3.9035 16.7479 4.43114 16.4225 4.75657L11.1784 10.0007L16.4225 15.2447C16.7479 15.5702 16.7479 16.0978 16.4225 16.4232C16.0971 16.7487 15.5694 16.7487 15.244 16.4232L9.99992 11.1792L4.75584 16.4232C4.4304 16.7487 3.90277 16.7487 3.57733 16.4232C3.25189 16.0978 3.25189 15.5702 3.57733 15.2447L8.82141 10.0007L3.57733 4.75657C3.25189 4.43114 3.25189 3.9035 3.57733 3.57806Z"
                                fill="#64748B"
                            />
                        </svg>
                    </button>
                </div>

                <!-- Illustration area -->
                <div
                    class="flex h-[400px] bg-slate-50 items-center justify-end"
                >
                    <img
                        :src="mobileImgSrc(curStep.imgKey)"
                        :alt="curStep.label"
                        class="h-full object-contain"
                    />
                </div>

                <!-- Content block -->
                <div
                    :class="[
                        'flex flex-col items-start gap-4 w-full px-6 pt-8 flex-1 self-stretch',
                        'pb-[calc(env(safe-area-inset-bottom,0)+96px)]',
                    ]"
                >
                    <div class="flex items-center justify-center gap-2 w-full">
                        <Icon
                            :icon="curStep.icon"
                            :width="20"
                            :height="20"
                            containerClass="text-[#FD421D]"
                        />
                        <span
                            class="flex-1 text-[#FD421D] text-[14px] leading-5 font-medium"
                        >
                            {{ curStep.label }}
                        </span>
                    </div>
                    <h3
                        class="text-2xl leading-8 font-semibold text-slate-800 w-full"
                    >
                        {{ curStep.title }}
                    </h3>
                    <p class="text-[14px] leading-5 text-slate-500 w-full">
                        {{ curStep.desc }}
                    </p>
                </div>
            </div>

            <!-- Actions: bottom bar (mobile-only, pinned) -->
            <div
                class="sm:hidden z-[100001] bg-white dark:bg-slate-800 px-6 pt-2 pb-[calc(env(safe-area-inset-bottom,0)+16px)] border-t border-slate-100 fixed left-0 right-0 bottom-0"
            >
                <div
                    aria-hidden="true"
                    class="pointer-events-none absolute -top-8 left-0 right-0 h-8 bg-gradient-to-b from-transparent via-white/80 to-white"
                ></div>
                <div class="mx-auto max-w-screen-sm">
                    <div class="flex items-center gap-4 w-full">
                        <button
                            v-if="mobileIndex > 0"
                            type="button"
                            @click="mobilePrev"
              class="inline-flex justify-center items-center gap-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-2.5 shadow-sm dark:shadow-slate-900/50 text-[14px] leading-5 font-medium text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700 flex-1"
                        >
                            Previous
                        </button>
                        <button
                            type="button"
                            @click="mobilePrimary"
                            class="inline-flex justify-center items-center gap-2 rounded bg-[#FD421D] px-4 py-2.5 shadow-sm text-[14px] leading-5 font-medium text-white hover:brightness-95 flex-1"
                        >
                            {{ isLast ? 'Finish' : 'Next' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script setup>
import { onMounted, onBeforeUnmount, computed, ref, watch } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { storeToRefs } from 'pinia';
import { Apii } from '@/services/api.js';
import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';
import tourTracking from '@/utils/tourTracking.js';
import Icon from '@/components/ui/icon/icon.vue';
import { useAppStore } from '@/store/app';

const appStore = useAppStore();
const { activePopup } = storeToRefs(appStore);

let tour = null;
let firstTargetSel = null;
const finishing = ref(false);
const ApiService = new Apii();

const page = usePage();

const backendOnboarded = computed(() => {
    const p = page.props.value || {};
    if (p.auth?.user && typeof p.auth.user.onboarded !== 'undefined')
        return !!p.auth.user.onboarded;
    if (p.userData && typeof p.userData.onboarded !== 'undefined')
        return !!p.userData.onboarded;
    return false;
});

const isSubscribed = computed(() => {
    const p = page.props || {};
    if (p.userData && typeof p.userData.is_subscribed !== 'undefined')
        return !!p.userData.is_subscribed;
    if (p.auth?.user && typeof p.auth.user.is_subscribed !== 'undefined')
        return !!p.auth.user.is_subscribed;
    return false;
});

// The my-spending flag (admins and staff): the step points at the wallet
// nav, which everyone has, so it is filtered on the flag instead.
const spendingAvailable = computed(
    () => page.props?.userData?.spending_available === true
);

const shouldShowTravelPlusStep = computed(() => {
    const p = page.props.value || page.props || {};
    const u = p.userData || {};
    const stepSeen = !!(
        u.travel_plus_tour_step_seen || u.travel_plus_tour_step_seen_at
    );
    const tourDismissed = !!u.tour_dismissed;
    const tourTeaserEligible = !!(
        u.tour_teaser_eligible === true ||
        u.tour_teaser_eligible === 1 ||
        u.tour_teaser_eligible === '1'
    );
    return !stepSeen && (tourDismissed || !tourTeaserEligible);
});

// Track if we've already launched the standalone 10xTravel+ step
const travelPlusStepLaunched = ref(false);
// Track if we're in "travel-plus only" mode (standalone popup, not full tour)
const travelPlusOnlyMode = ref(false);

// -----------------
// Mobile-only state
// -----------------
const mobileVisible = ref(false);
const mobileIndex = ref(0);
const allMobileSteps = [
    {
        id: 'wallet',
        selector: '.nav-my-wallet',
        imgKey: 'wallet',
        icon: 'wallet',
        label: 'My Wallet',
        title: 'All your cards in one place',
        desc: 'Add your cards to My10x to track points, check 5/24 status, and get new card recommendations. Connect securely or add them manually.',
    },
    {
        id: 'spending',
        selector: '.nav-my-wallet',
        requiresSpending: true,
        imgKey: 'spending',
        icon: 'graph',
        label: 'My Spending',
        title: 'See where your money goes',
        desc: 'Every transaction from your connected cards in one feed, with estimated rewards for each purchase. See which card earns more and spend smarter.',
    },
    {
        id: 'travel-plus',
        selector: '.nav-10x-travel-plus',
        requiresPlus: true,
        imgKey: 'travel-plus',
        icon: 'star',
        label: '10xTravel+',
        title: 'Explore your 10xTravel+ membership',
        desc: 'Unlock the full power of your rewards with automated tools and a community of travel experts.',
    },
    {
        id: 'travel-plus-free',
        selector: '.nav-10x-travel-plus',
        requiresFree: true,
        imgKey: 'travel-plus-free',
        icon: 'star',
        label: '10xTravel+',
        title: 'Discover 10xTravel+',
        desc: 'Upgrade to unlock automated rewards tools, exclusive benefits, and a community of travel experts.',
    },
    {
        id: 'course',
        selector: '.nav-10x-course',
        imgKey: 'course',
        icon: 'courses',
        label: '10x Course',
        title: 'Ready to travel more and spend less?',
        desc: "Discover how to earn free flights, unlock travel perks, and explore more, all without breaking the bank. Jump into the course and see what's possible.",
    },
    {
        id: 'benefits',
        selector: '.nav-card-benefits',
        imgKey: 'benefits',
        icon: 'coin',
        label: 'Benefits',
        title: 'Your card benefits, all in one place',
        desc: "Track every benefit and reward across your cards. Mark used benefits and never miss what's still available.",
    },
    {
        id: 'points',
        selector: '.nav-my-points',
        imgKey: 'points',
        icon: 'points',
        label: 'My Points',
        title: 'Stay on top of your points',
        desc: "Use My Points to track your loyalty balances. Connect accounts for automatic updates or add them manually, we'll remind you when it's time.",
    },
    {
        id: 'nextcard',
        selector: '.nav-my-next-card',
        imgKey: 'nextcard',
        icon: 'credit-card',
        label: 'My Next Card',
        title: 'Smart picks, just for you',
        desc: 'Our tool reviews your cards, points, and eligibility to recommend the best next card, all in one click.',
    },
    {
        id: 'redemptions',
        selector: '.nav-redemption-tracker',
        imgKey: 'redemptions',
        icon: 'redemption-tracker',
        label: 'Redemption Tracker',
        title: 'Understand the value of your points & miles',
        desc: 'Track your points and miles redemptions to improve your strategy and remember your adventures.',
    },
];

const mobileSteps = computed(() => {
    // In travel-plus only mode, return only the single travel-plus step
    if (travelPlusOnlyMode.value) {
        const stepId = isSubscribed.value ? 'travel-plus' : 'travel-plus-free';
        const singleStep = allMobileSteps.find((s) => s.id === stepId);
        return singleStep ? [singleStep] : [];
    }
    try {
        return allMobileSteps.filter(
            (step) =>
                (!step.requiresPlus || isSubscribed.value) &&
                (!step.requiresFree || !isSubscribed.value) &&
                (!step.requiresSpending || spendingAvailable.value) &&
                document.querySelector(step.selector)
        );
    } catch (_) {
        return allMobileSteps; // fallback to all steps if DOM query fails
    }
});
const curStep = computed(
    () => mobileSteps.value[mobileIndex.value] || mobileSteps.value[0]
);
const isLast = computed(
    () => mobileIndex.value >= mobileSteps.value.length - 1
);

const isShortScreen = ref(false);
function computeShortScreen() {
    try {
        const h = Math.min(
            window.innerHeight || Infinity,
            document.documentElement?.clientHeight || Infinity
        );
        isShortScreen.value = h <= 750;
    } catch (_) {
        isShortScreen.value = false;
    }
}

function mobileImgSrc(key) {
    try {
        const m = window.__tourImgsMobile || {};
        const d = window.__tourImgs || {};
        return m[key] || d[key] || '';
    } catch (_) {
        return '';
    }
}
function openMobileTour() {
    mobileIndex.value = 0;
    mobileVisible.value = true;
    try {
        computeShortScreen();
    } catch (_) {}
    try {
        tourTracking.trackTourStarted();
    } catch (_) {}
    try {
        window.dispatchEvent(new CustomEvent('user-tour-started'));
    } catch (_) {}
    try {
        tourTracking.trackStepViewed(0, mobileSteps.value[0]?.id);
    } catch (_) {}
}
function mobilePrev() {
    mobileIndex.value = Math.max(0, mobileIndex.value - 1);
}
function mobilePrimary() {
    try {
        tourTracking.trackStepCompleted(mobileIndex.value, curStep.value.id);
    } catch (_) {}
    // In travel-plus only mode, always finish (single step)
    if (travelPlusOnlyMode.value || isLast.value) {
        finishMobile();
    } else {
        mobileIndex.value = Math.min(
            mobileSteps.value.length - 1,
            mobileIndex.value + 1
        );
        try {
            tourTracking.trackStepViewed(mobileIndex.value, curStep.value.id);
        } catch (_) {}
    }
}
async function mobileCancel() {
    try {
        tourTracking.trackTourCancelled(mobileIndex.value, curStep.value.id);
    } catch (_) {}
    const wasTravelPlusOnly = travelPlusOnlyMode.value;
    mobileVisible.value = false;
    travelPlusOnlyMode.value = false; // Reset mode on cancel
    if (wasTravelPlusOnly) {
        await markTravelPlusStepSeen();
    }
    try {
        window.dispatchEvent(new CustomEvent('user-tour-cancelled'));
    } catch (_) {}
}
function finishMobile() {
    mobileVisible.value = false;
    mobileIndex.value = 0; // Reset to step 0 immediately to prevent flash
    // In travel-plus only mode, call the specific finish handler
    if (travelPlusOnlyMode.value) {
        finishTravelPlusStep();
        travelPlusOnlyMode.value = false; // Reset mode
    } else {
        finishTour();
    }
}

function isMobile() {
    try {
        const mq =
            window.matchMedia && window.matchMedia('(max-width: 639.98px)');
        if (mq && typeof mq.matches === 'boolean') return mq.matches;
    } catch (_) {}
    try {
        const w = Math.min(
            window.innerWidth || Infinity,
            document.documentElement?.clientWidth || Infinity
        );
        return w < 640;
    } catch (_) {
        return (window.innerWidth || 0) < 640;
    }
}

function emphasizeTarget(selector) {
    try {
        const li = document.querySelector(selector)?.closest('li');
        li && li.classList.add('bg-navy-600');
    } catch (_) {}
}
function deEmphasizeTarget(selector) {
    try {
        const li = document.querySelector(selector)?.closest('li');
        li && li.classList.remove('bg-navy-600');
    } catch (_) {}
}

function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
}
function getOrCreateNotch(root) {
    let notch = root.querySelector('.tour-notch');
    if (!notch) {
        notch = document.createElement('div');
        notch.className =
            'tour-notch absolute w-7 h-[18px] shrink-0 bg-white rounded-md';
        notch.style.left = '-9px';
        root.appendChild(notch);
    }
    return notch;
}
function forceXNudge(step, dx = 0) {
    try {
        if (!step || !dx) return;
        const root = step.getElement?.();
        if (!root) return;
        const cs = window.getComputedStyle(root);
        const curLeft = parseFloat(root.style.left || cs.left || '0') || 0;
        root.style.left = `${Math.round(curLeft + dx)}px`;
    } catch (_) {}
}

// Vertical nudge: adjust the popover top position after Popper placement
function forceYNudge(step, dy = 0) {
    try {
        if (!step || !dy) return;
        const root = step.getElement?.();
        if (!root) return;
        const cs = window.getComputedStyle(root);
        const curTop = parseFloat(root.style.top || cs.top || '0') || 0;
        root.style.top = `${Math.round(curTop + dy)}px`;
    } catch (_) {}
}

function ensureRightClearance(step, margin = 16) {
    try {
        if (!step) return;
        const root = step.getElement?.();
        if (!root) return;
        const sidebar = document.querySelector('#sidebar');
        const panelRect = root.getBoundingClientRect();
        const edgeRight = sidebar ? sidebar.getBoundingClientRect().right : 0;
        const minLeft = edgeRight + margin;
        const delta = Math.ceil(minLeft - panelRect.left);
        if (delta > 0) {
            step.__forcedDx = (step.__forcedDx || 0) + delta;
            forceXNudge(step, delta);
        }
    } catch (_) {}
}

function positionArrowFor(step) {
    try {
        const root = step?.getElement?.();
        const sel = step?.options?.attachTo?.element;
        const base = sel ? document.querySelector(sel) : null;
        const target = base?.closest('li') || base;
        if (!root || !target) return;
        const rTarget = target.getBoundingClientRect();
        const rPanel = root.getBoundingClientRect();
        const targetCenterY = rTarget.top + rTarget.height / 2;
        const R = 16,
            AR = 9;
        let arrowY = targetCenterY - rPanel.top;
        arrowY = clamp(arrowY, R + AR, rPanel.height - R - AR);
        const notch = getOrCreateNotch(root);
        notch.style.left = '';
        notch.style.right = '';
        notch.style.top = '';
        notch.style.bottom = '';
        const placement = root.getAttribute('data-popper-placement') || 'right';
        if (placement.startsWith('right')) {
            notch.style.top = `${Math.round(arrowY)}px`;
            notch.style.left = '-9px';
            notch.style.transform = 'translateY(-50%) rotate(45deg)';
        } else if (placement.startsWith('left')) {
            notch.style.top = `${Math.round(arrowY)}px`;
            notch.style.right = '-9px';
            notch.style.transform = 'translateY(-50%) rotate(45deg)';
        } else if (placement.startsWith('top')) {
            notch.style.left = '50%';
            notch.style.bottom = '-9px';
            notch.style.transform = 'translateX(-50%) rotate(45deg)';
        } else if (placement.startsWith('bottom')) {
            notch.style.left = '50%';
            notch.style.top = '-9px';
            notch.style.transform = 'translateX(-50%) rotate(45deg)';
        }
    } catch (_) {}
}

function bindArrowReposition(step) {
    const handler = () => {
        positionArrowFor(step);
        try {
            if (typeof step.__forcedDx === 'number' && step.__forcedDx !== 0)
                forceXNudge(step, step.__forcedDx);
        } catch (_) {}
    };
    window.addEventListener('scroll', handler, { passive: true });
    window.addEventListener('resize', handler, { passive: true });
    try {
        const sidebar = document.querySelector('#sidebar');
        if (sidebar) {
            sidebar.addEventListener('scroll', handler, { passive: true });
            step.__arrowSidebarEl = sidebar;
        }
    } catch (_) {}
    step.__arrowHandler = handler;
    requestAnimationFrame(handler);
}
function unbindArrowReposition(step) {
    const h = step?.__arrowHandler;
    if (h) {
        window.removeEventListener('scroll', h);
        window.removeEventListener('resize', h);
        try {
            if (step.__arrowSidebarEl) {
                step.__arrowSidebarEl.removeEventListener('scroll', h);
                step.__arrowSidebarEl = null;
            }
        } catch (_) {}
        step.__arrowHandler = null;
    }
}

function buildTour() {
    if (isMobile()) return;
    if (tour) {
        try {
            tour.off && tour.off('*');
        } catch (_) {}
        try {
            tour.cancel && tour.cancel();
        } catch (_) {}
        tour = null;
    }
    tour = new Shepherd.Tour({
        useModalOverlay: true,
        exitOnEsc: true,
        defaultStepOptions: {
            cancelIcon: { enabled: false },
            scrollTo: false,
            canClickTarget: false,
            arrow: false,
            classes:
                'my10x-tour p-0 w-full !max-w-[500px] rounded-md bg-white !shadow-none ring-1 ring-slate-200 transition-all duration-300 ease-in-out',
            modalOverlayOpeningPadding: 10,
            modalOverlayOpeningRadius: 0,
        },
    });

    const headerSection = (imgKey = '', imgAlt = '') => `
    <div class="flex w-full h-full px-5 pb-0 justify-center items-start content-start flex-wrap bg-[linear-gradient(189deg,#FCFEFF_60.09%,#FFFEFC_96.6%)] relative">
      <button type="button" class="flex p-2 items-center rounded-full absolute right-0 top-0 hover:bg-slate-100 dark:bg-slate-700 tour-close" aria-label="Close">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 text-slate-500 dark:text-slate-400"><path fill-rule="evenodd" d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z" clip-rule="evenodd" /></svg>
      </button>
      <div class="w-full flex items-start justify-center">
        <div class="w-full h-[264px] rounded-xl overflow-hidden">
          <img src="" data-img-key="${imgKey}" alt="${imgAlt}" class="block w-full h-full object-cover" />
        </div>
      </div>
      <div class="pointer-events-none absolute inset-x-0 bottom-[-6px] h-2 bg-gradient-to-b from-slate-400/15 via-slate-300/8 to-transparent"></div>
    </div>
  `;

    const contentSection = (title, desc, step, total) => `
    <div class="flex w-full flex-col items-start gap-0 bg-white dark:bg-slate-800 px-5 pb-3">
      <div class="flex flex-col justify-center items-start gap-3 w-full pt-8">
        <h3 class="text-[18px] leading-7 font-medium text-slate-800 dark:text-slate-100">${title}</h3>
        <p class="text-[14px] text-slate-500 dark:text-slate-400">${desc}</p>
      </div>
      <div class="flex items-center justify-between w-full pt-6">
        <span class="text-[14px] leading-5 text-slate-500 dark:text-slate-400">${step} of ${total}</span>
        <div class="flex items-center gap-3">
          <button type="button" class="tour-prev inline-flex items-center gap-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-2 shadow-sm text-sm font-medium text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700">Previous</button>
          <button type="button" class="tour-primary inline-flex justify-center items-center gap-2 rounded-md bg-[#FD421D] px-4 py-2 shadow-sm text-sm font-medium text-white hover:brightness-95">Next</button>
        </div>
      </div>
    </div>
  `;

    const stepContent = (imgKey, imgAlt, title, desc, step, total) => `
    <div class="flex w-full flex-col items-start rounded-lg">
      ${headerSection(imgKey, imgAlt)}
      ${contentSection(title, desc, step, total)}
    </div>
  `;

    const MIN_TOP = 112;
    const GAP = 16;
    const GLOBAL_X_NUDGE = 72;
    const popperFor = (skid = 0, dx = 24, dy = 0) => ({
        strategy: 'fixed',
        modifiers: [
            {
                name: 'offset',
                options: { offset: [skid, GAP + GLOBAL_X_NUDGE + dx] },
            },
            {
                name: 'nudgeY',
                enabled: dy !== 0,
                phase: 'main',
                fn({ state }) {
                    if (state.modifiersData?.popperOffsets) {
                        state.modifiersData.popperOffsets.y += dy;
                    }
                },
            },
            {
                name: 'preventOverflow',
                options: {
                    boundary: 'viewport',
                    mainAxis: true,
                    altAxis: true,
                    tether: true,
                    padding: { top: 24, bottom: 24, left: 24, right: 0 },
                },
            },
            {
                name: 'nudgeX',
                enabled: dx !== 0,
                phase: 'main',
                fn({ state }) {
                    if (state.modifiersData?.popperOffsets) {
                        state.modifiersData.popperOffsets.x += dx;
                    }
                },
            },
            { name: 'flip', enabled: false },
            {
                name: 'tourArrow',
                enabled: true,
                phase: 'write',
                fn() {
                    try {
                        positionArrowFor(tour?.currentStep);
                    } catch (_) {}
                },
            },
            { name: 'eventListeners', options: { scroll: true, resize: true } },
        ],
    });

    // Build steps dynamically based on available menu items
    const availableSteps = [
        {
            id: 'wallet',
            selector: '.nav-my-wallet',
            title: 'Wallet preview',
            subtitle: 'See all your cards in one place',
            desc: 'Add your cards to My10x to track points, check 5/24 status, and get new card recommendations. Connect securely or add them manually.',
            popperOptions: popperFor(0, 24),
        },
        {
            id: 'spending',
            selector: '.nav-my-wallet',
            requiresSpending: true,
            title: 'My Spending preview',
            subtitle: 'See where your money goes',
            desc: 'Find My Spending under My Wallet — every transaction from your connected cards in one feed, with estimated rewards for each purchase. See which card earns more and spend smarter.',
            popperOptions: popperFor(0, 24),
        },
        {
            id: 'travel-plus',
            selector: '.nav-10x-travel-plus',
            requiresPlus: true,
            title: '10xTravel+ preview',
            subtitle: 'Explore your 10xTravel+ membership',
            desc: 'Unlock the full power of your rewards with automated tools and a community of travel experts.',
            popperOptions: popperFor(),
        },
        {
            id: 'travel-plus-free',
            selector: '.nav-10x-travel-plus',
            requiresFree: true,
            title: '10xTravel+ preview',
            subtitle: 'Discover 10xTravel+',
            desc: 'Upgrade to unlock automated rewards tools, exclusive benefits, and a community of travel experts.',
            popperOptions: popperFor(),
        },
        {
            id: 'course',
            selector: '.nav-10x-course',
            title: '10x Course preview',
            subtitle: 'Ready to travel more and spend less?',
            desc: "Discover how to earn free flights, unlock travel perks, and explore more, all without breaking the bank. Jump into the course and see what's possible.",
            popperOptions: popperFor(),
        },
        {
            id: 'benefits',
            selector: '.nav-card-benefits',
            title: 'Benefits preview',
            subtitle: 'Your card benefits, all in one place',
            desc: "Track every benefit and reward across your cards. Mark used benefits and never miss what's still available.",
            popperOptions: popperFor(),
        },
        {
            id: 'points',
            selector: '.nav-my-points',
            title: 'My Points preview',
            subtitle: 'Stay on top of your points',
            desc: "Use My Points to track your loyalty balances. Connect accounts for automatic updates or add them manually, we'll remind you when it's time.",
            popperOptions: popperFor(),
        },
        {
            id: 'nextcard',
            selector: '.nav-my-next-card',
            title: 'My Next Card preview',
            subtitle: 'Smart picks, just for you',
            desc: 'Our tool reviews your cards, points, and eligibility to recommend the best next card, all in one click.',
            popperOptions: popperFor(),
        },
        {
            id: 'redemptions',
            selector: '.nav-redemption-tracker',
            title: 'Redemption preview',
            subtitle: 'Understand the value of your points & miles',
            desc: 'Track your points and miles redemptions to improve your strategy and remember your adventures.',
            popperOptions: popperFor(-12),
        },
    ];

    const enabledSteps = availableSteps.filter(
        (step) =>
            (!step.requiresPlus || isSubscribed.value) &&
            (!step.requiresFree || !isSubscribed.value) &&
            (!step.requiresSpending || spendingAvailable.value) &&
            document.querySelector(step.selector)
    );
    const totalSteps = enabledSteps.length;

    const steps = enabledSteps.map((step, index) => ({
        id: step.id,
        attachTo: { element: step.selector, on: 'right-start' },
        text: stepContent(
            step.id,
            step.title,
            step.subtitle,
            step.desc,
            index + 1,
            totalSteps
        ),
        popperOptions: step.popperOptions,
    }));

    try {
        firstTargetSel = steps[0]?.attachTo?.element || null;
    } catch (_) {
        firstTargetSel = null;
    }

    steps.forEach((s) => {
        if (!s.attachTo || document.querySelector(s.attachTo.element)) {
            tour.addStep({
                ...s,
                when: {
                    show: () => {
                        // Track step viewed event
                        try {
                            const stepIndex = getSafeStepIndex();
                            const stepName = s.id;
                            tourTracking.trackStepViewed(stepIndex, stepName);
                        } catch (_) {}

                        emphasizeTarget(s.attachTo?.element);
                        const el = tour?.currentStep?.getElement?.();
                        if (el) {
                            el.style.opacity = '0';
                            el.style.transform = 'translateY(10px)';
                            setTimeout(() => {
                                el.style.transition =
                                    'opacity 0.3s ease-out, transform 0.3s ease-out';
                                el.style.opacity = '1';
                                el.style.transform = 'translateY(0)';
                            }, 10);
                        }
                        try {
                            const target =
                                document
                                    .querySelector(s.attachTo?.element)
                                    ?.closest('li') ||
                                document.querySelector(s.attachTo?.element);
                            const sidebar = document.querySelector('#sidebar');
                            if (target && sidebar) {
                                const sRect = sidebar.getBoundingClientRect();
                                const tRect = target.getBoundingClientRect();
                                const tTopRel =
                                    tRect.top - sRect.top + sidebar.scrollTop;
                                const tCenter =
                                    tTopRel +
                                    (target.offsetHeight || tRect.height) / 2;
                                const desired = Math.max(
                                    0,
                                    Math.min(
                                        tCenter - sidebar.clientHeight / 2,
                                        Math.max(
                                            0,
                                            sidebar.scrollHeight -
                                                sidebar.clientHeight
                                        )
                                    )
                                );
                                sidebar.scrollTo({
                                    top: desired,
                                    behavior: 'auto',
                                });
                            }
                        } catch (_) {}
                        // Base nudges and clearance check so the popover clears the dashboard content edge
                        try {
                            if (tour?.currentStep) {
                                tour.currentStep.__forcedDx = 32;
                                tour.currentStep.__forcedDy = -12;
                            }
                        } catch (_) {}
                        requestAnimationFrame(() =>
                            requestAnimationFrame(() => {
                                const root =
                                    tour?.currentStep &&
                                    typeof tour.currentStep.getElement ===
                                        'function'
                                        ? tour.currentStep.getElement()
                                        : document.querySelector(
                                              '.shepherd-element.shepherd-open'
                                          ) ||
                                          document.querySelector(
                                              '.shepherd-element.is-open'
                                          );
                                try {
                                    if (
                                        typeof tour?.currentStep?.__forcedDx ===
                                            'number' &&
                                        tour.currentStep.__forcedDx
                                    ) {
                                        forceXNudge(
                                            tour.currentStep,
                                            tour.currentStep.__forcedDx
                                        );
                                    }
                                    if (
                                        typeof tour?.currentStep?.__forcedDy ===
                                            'number' &&
                                        tour.currentStep.__forcedDy
                                    ) {
                                        forceYNudge(
                                            tour.currentStep,
                                            tour.currentStep.__forcedDy
                                        );
                                    }
                                    ensureRightClearance(tour.currentStep, 16);
                                    positionArrowFor(tour.currentStep);
                                    bindArrowReposition(tour.currentStep);
                                } catch (_) {}
                                const prevBtn =
                                    root?.querySelector('.tour-prev');
                                const primaryBtn =
                                    root?.querySelector('.tour-primary');
                                const closeBtn =
                                    root?.querySelector('.tour-close');
                                try {
                                    const img =
                                        root?.querySelector(
                                            'img[data-img-key]'
                                        );
                                    const key =
                                        img?.getAttribute('data-img-key') || '';
                                    const map = window.__tourImgs || {};
                                    if (img && key && map[key])
                                        img.setAttribute('src', map[key]);
                                    if (img) {
                                        const rerun = () => {
                                            try {
                                                positionArrowFor(
                                                    tour.currentStep
                                                );
                                            } catch (_) {}
                                        };
                                        if (!img.complete)
                                            img.addEventListener(
                                                'load',
                                                rerun,
                                                { once: true }
                                            );
                                        else requestAnimationFrame(rerun);
                                    }
                                } catch (_) {}
                                try {
                                    const idx = getSafeStepIndex();
                                    const total = tour?.steps?.length || 0;
                                    if (prevBtn) {
                                        if (idx <= 0)
                                            prevBtn.classList.add('hidden');
                                        else prevBtn.classList.remove('hidden');
                                        prevBtn.addEventListener(
                                            'click',
                                            () => {
                                                try {
                                                    tour.back();
                                                } catch (_) {}
                                            },
                                            { once: true }
                                        );
                                    }
                                    if (primaryBtn) {
                                        primaryBtn.textContent =
                                            idx >= total - 1
                                                ? 'Finish'
                                                : 'Next';
                                        primaryBtn.addEventListener(
                                            'click',
                                            () => {
                                                try {
                                                    const cur =
                                                        getSafeStepIndex();
                                                    const last =
                                                        (tour?.steps?.length ||
                                                            0) - 1;

                                                    // Track step completion before moving to next step
                                                    const currentStep =
                                                        tour?.currentStep;
                                                    if (
                                                        currentStep &&
                                                        currentStep.options &&
                                                        currentStep.options.id
                                                    ) {
                                                        tourTracking.trackStepCompleted(
                                                            cur,
                                                            currentStep.options
                                                                .id
                                                        );
                                                    }

                                                    if (cur >= last)
                                                        finishTour();
                                                    else tour.next();
                                                } catch (_) {}
                                            },
                                            { once: true }
                                        );
                                    }
                                } catch (_) {}
                                setTimeout(() => {
                                    try {
                                        positionArrowFor(tour.currentStep);
                                    } catch (_) {}
                                }, 120);
                                setTimeout(() => {
                                    try {
                                        positionArrowFor(tour.currentStep);
                                    } catch (_) {}
                                }, 260);
                                if (closeBtn) {
                                    closeBtn.addEventListener(
                                        'click',
                                        () => {
                                            try {
                                                tour.cancel();
                                            } catch (_) {}
                                        },
                                        { once: true }
                                    );
                                }
                            })
                        );
                    },
                    hide: () => {
                        const el = tour?.currentStep?.getElement?.();
                        if (el) {
                            el.style.transition = 'opacity 0.2s ease-in';
                            el.style.opacity = '0';
                        }
                        deEmphasizeTarget(s.attachTo?.element);
                        try {
                            unbindArrowReposition(tour.currentStep);
                        } catch (_) {}
                    },
                },
                buttons: [],
            });
        }
    });

    tour.on('cancel', () => {
        // Track tour cancellation with current step context
        const currentStep = tour?.currentStep;
        const stepIndex = getSafeStepIndex();
        const stepName = currentStep?.options?.id || null;
        tourTracking.trackTourCancelled(stepIndex, stepName);

        if (tour?.currentStep?.options?.attachTo?.element) {
            deEmphasizeTarget(tour.currentStep.options.attachTo.element);
        }
        window.dispatchEvent(new CustomEvent('user-tour-cancelled'));
    });
    tour.on('complete', () => finishTour());
}

function getSafeStepIndex() {
    try {
        return tour?.steps?.indexOf(tour.currentStep) ?? 0;
    } catch (_) {
        return 0;
    }
}

function startTour(fromIndex = 0) {
    if (isMobile()) {
        openMobileTour();
        return;
    }
    if (!tour) buildTour();
    try {
        if (typeof window.openSidebar === 'function') window.openSidebar();
    } catch (_) {}
    const first = firstTargetSel;
    const waitForTarget = (sel, tries = 10) =>
        new Promise((resolve) => {
            const ok = () => {
                const el = document.querySelector(sel);
                return el && el.offsetWidth > 0 && el.offsetHeight > 0
                    ? el
                    : null;
            };
            const tick = () => {
                const el = ok();
                if (el || tries <= 0) return resolve();
                setTimeout(() => {
                    tries--;
                    tick();
                }, 60);
            };
            tick();
        });
    waitForTarget(first).then(() => {
        // Track tour started event
        tourTracking.trackTourStarted();

        try {
            tour.show(fromIndex);
        } catch {
            tour.start();
        }
        window.dispatchEvent(new CustomEvent('user-tour-started'));
    });
}

async function finishTour() {
    if (finishing.value) return;
    finishing.value = true;
    try {
        if (tour?.currentStep?.options?.attachTo?.element)
            deEmphasizeTarget(tour.currentStep.options.attachTo.element);
    } catch (_) {}
    try {
        if (tour) {
            try {
                tour.off && tour.off('*');
            } catch (_) {}
            tour.cancel && tour.cancel();
        }
    } catch (_) {}
    window.dispatchEvent(new CustomEvent('user-tour-finished'));

    try {
        await ApiService.apiCall('/tour/dismiss', 'post');
    } catch (error) {
        console.error('Failed to dismiss tour:', error);
        // ignore network error, still attempt to refresh props
    }
    try {
        // Hide teaser immediately and force a full reload to refresh Inertia props
        if (typeof window.hideTourTeaser === 'function')
            window.hideTourTeaser();
        window.location.reload();
    } finally {
        finishing.value = false;
    }
}

async function finishTravelPlusStep() {
    if (finishing.value) return;
    finishing.value = true;
    try {
        if (tour?.currentStep?.options?.attachTo?.element)
            deEmphasizeTarget(tour.currentStep.options.attachTo.element);
    } catch (_) {}
    try {
        if (tour) {
            try {
                tour.off && tour.off('*');
            } catch (_) {}
            tour.cancel && tour.cancel();
        }
    } catch (_) {}

    await markTravelPlusStepSeen();
    finishing.value = false;
}

async function markTravelPlusStepSeen() {
    try {
        await ApiService.apiCall('/tour/travel-plus-step/seen', 'post');
        const p = page.props?.value || page.props || {};
        if (p.userData) {
            p.userData.travel_plus_tour_step_seen = true;
            if (!p.userData.travel_plus_tour_step_seen_at) {
                p.userData.travel_plus_tour_step_seen_at =
                    new Date().toISOString();
            }
        }
    } catch (error) {
        console.error('Failed to mark 10xTravel+ step seen:', error);
    }
}

function launchTravelPlusOnlyStep() {
    if (isMobile()) {
        // For mobile, show just the travel-plus step
        const stepData = isSubscribed.value
            ? allMobileSteps.find((s) => s.id === 'travel-plus')
            : allMobileSteps.find((s) => s.id === 'travel-plus-free');
        if (stepData) {
            travelPlusOnlyMode.value = true; // Enable single-step mode
            mobileIndex.value = 0;
            mobileVisible.value = true;
            try {
                tourTracking.trackStepViewed(0, stepData.id);
            } catch (_) {}
        }
        return;
    }

    // Desktop: build a mini Shepherd tour with just the 10xTravel+ step
    if (tour) {
        try {
            tour.off && tour.off('*');
        } catch (_) {}
        try {
            tour.cancel && tour.cancel();
        } catch (_) {}
        tour = null;
    }

    const selector = '.nav-10x-travel-plus';
    if (!document.querySelector(selector)) return;

    const stepId = isSubscribed.value ? 'travel-plus' : 'travel-plus-free';
    const stepTitle = isSubscribed.value
        ? 'Explore your 10xTravel+ membership'
        : 'Discover 10xTravel+';
    const stepDesc = isSubscribed.value
        ? 'Unlock the full power of your rewards with automated tools and a community of travel experts.'
        : 'Upgrade to unlock automated rewards tools, exclusive benefits, and a community of travel experts.';

    tour = new Shepherd.Tour({
        useModalOverlay: true,
        exitOnEsc: true,
        defaultStepOptions: {
            cancelIcon: { enabled: false },
            scrollTo: false,
            canClickTarget: false,
            arrow: false,
            classes:
                'my10x-tour p-0 w-full !max-w-[500px] rounded-md bg-white !shadow-none ring-1 ring-slate-200 transition-all duration-300 ease-in-out',
            modalOverlayOpeningPadding: 10,
            modalOverlayOpeningRadius: 0,
        },
    });

    tour.on('cancel', () => {
        if (finishing.value) return;
        markTravelPlusStepSeen();
    });

    const headerSection = (imgKey = '', imgAlt = '') => `
    <div class="flex w-full h-full px-5 pb-0 justify-center items-start content-start flex-wrap bg-[linear-gradient(189deg,#FCFEFF_60.09%,#FFFEFC_96.6%)] relative">
      <button type="button" class="flex p-2 items-center rounded-full absolute right-0 top-0 hover:bg-slate-100 tour-close" aria-label="Close">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 text-slate-500"><path fill-rule="evenodd" d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z" clip-rule="evenodd" /></svg>
      </button>
      <div class="w-full flex items-start justify-center">
        <div class="w-full h-[264px] rounded-xl overflow-hidden">
          <img src="" data-img-key="${imgKey}" alt="${imgAlt}" class="block w-full h-full object-cover" />
        </div>
      </div>
      <div class="pointer-events-none absolute inset-x-0 bottom-[-6px] h-2 bg-gradient-to-b from-slate-400/15 via-slate-300/8 to-transparent"></div>
    </div>
  `;

    const contentSection = (title, desc) => `
    <div class="flex w-full flex-col items-start gap-0 bg-white dark:bg-slate-800 px-5 pb-3">
      <div class="flex flex-col justify-center items-start gap-3 w-full pt-8">
        <h3 class="text-[18px] leading-7 font-medium text-slate-800">${title}</h3>
        <p class="text-[14px] text-slate-500">${desc}</p>
      </div>
      <div class="flex items-center justify-end w-full pt-6">
        <button type="button" class="tour-primary inline-flex justify-center items-center gap-2 rounded-md bg-[#FD421D] px-4 py-2 shadow-sm text-sm font-medium text-white hover:brightness-95">Got it</button>
      </div>
    </div>
  `;

    const stepContent = `
    <div class="flex w-full flex-col items-start rounded-lg">
      ${headerSection(stepId, stepTitle)}
      ${contentSection(stepTitle, stepDesc)}
    </div>
  `;

    const GAP = 16;
    const GLOBAL_X_NUDGE = 72;
    const popperOptions = {
        strategy: 'fixed',
        modifiers: [
            {
                name: 'offset',
                options: { offset: [0, GAP + GLOBAL_X_NUDGE + 24] },
            },
            {
                name: 'preventOverflow',
                options: {
                    boundary: 'viewport',
                    mainAxis: true,
                    altAxis: true,
                    tether: true,
                    padding: { top: 24, bottom: 24, left: 24, right: 0 },
                },
            },
            { name: 'flip', enabled: false },
            {
                name: 'tourArrow',
                enabled: true,
                phase: 'write',
                fn() {
                    try {
                        positionArrowFor(tour?.currentStep);
                    } catch (_) {}
                },
            },
            { name: 'eventListeners', options: { scroll: true, resize: true } },
        ],
    };

    tour.addStep({
        id: stepId,
        attachTo: { element: selector, on: 'right-start' },
        text: stepContent,
        popperOptions,
        when: {
            show: () => {
                try {
                    tourTracking.trackStepViewed(0, stepId);
                } catch (_) {}
                emphasizeTarget(selector);
                const el = tour?.currentStep?.getElement?.();
                if (el) {
                    el.style.opacity = '0';
                    el.style.transform = 'translateY(10px)';
                    setTimeout(() => {
                        el.style.transition =
                            'opacity 0.3s ease-out, transform 0.3s ease-out';
                        el.style.opacity = '1';
                        el.style.transform = 'translateY(0)';
                    }, 10);
                }
                try {
                    if (tour?.currentStep) {
                        tour.currentStep.__forcedDx = 32;
                        tour.currentStep.__forcedDy = -12;
                    }
                } catch (_) {}
                requestAnimationFrame(() =>
                    requestAnimationFrame(() => {
                        const root = tour?.currentStep?.getElement?.();
                        try {
                            if (tour?.currentStep?.__forcedDx) {
                                forceXNudge(
                                    tour.currentStep,
                                    tour.currentStep.__forcedDx
                                );
                            }
                            if (tour?.currentStep?.__forcedDy) {
                                forceYNudge(
                                    tour.currentStep,
                                    tour.currentStep.__forcedDy
                                );
                            }
                            ensureRightClearance(tour.currentStep, 16);
                            positionArrowFor(tour.currentStep);
                            bindArrowReposition(tour.currentStep);
                        } catch (_) {}
                        const primaryBtn = root?.querySelector('.tour-primary');
                        const closeBtn = root?.querySelector('.tour-close');
                        const img = root?.querySelector('img[data-img-key]');
                        if (img) {
                            const key = img.getAttribute('data-img-key');
                            const src = window.__tourImgs?.[key] || '';
                            if (src) img.src = src;
                        }
                        if (primaryBtn) {
                            primaryBtn.addEventListener('click', () => {
                                try {
                                    tourTracking.trackStepCompleted(0, stepId);
                                } catch (_) {}
                                finishTravelPlusStep();
                            });
                        }
                        if (closeBtn) {
                            closeBtn.addEventListener('click', () => {
                                finishTravelPlusStep();
                            });
                        }
                    })
                );
            },
            hide: () => {
                deEmphasizeTarget(selector);
                unbindArrowReposition(tour?.currentStep);
            },
        },
    });

    try {
        if (typeof window.openSidebar === 'function') window.openSidebar();
    } catch (_) {}

    const waitForTarget = (sel, tries = 10) =>
        new Promise((resolve) => {
            const ok = () => {
                const el = document.querySelector(sel);
                return el && el.offsetWidth > 0 && el.offsetHeight > 0
                    ? el
                    : null;
            };
            const tick = () => {
                const el = ok();
                if (el || tries <= 0) return resolve();
                setTimeout(() => {
                    tries--;
                    tick();
                }, 60);
            };
            tick();
        });

    waitForTarget(selector).then(() => {
        try {
            tour.start();
        } catch (_) {}
    });
}

onMounted(() => {
    try {
        computeShortScreen();
    } catch (_) {}
    try {
        window.addEventListener('resize', computeShortScreen, {
            passive: true,
        });
    } catch (_) {}
    try {
        window.addEventListener('orientationchange', computeShortScreen, {
            passive: true,
        });
    } catch (_) {}
    // Desktop Shepherd tour images (masked per Figma)
    window.__tourImgs = {
        wallet: '/images/tour-guide/image_my_wallet.png',
        spending: '/images/tour-guide/image_my_spending.png',
        'travel-plus': '/images/tour-guide/image_10xplus_desktop.png',
        'travel-plus-free': '/images/tour-guide/image_10xplus_desktop.png',
        course: '/images/tour-guide/image_10x_course_Shepher.png',
        points: '/images/tour-guide/image_my_points_Shepher.png',
        nextcard: '/images/tour-guide/image_my_next_card_Shepher.png',
        redemptions: '/images/tour-guide/image_redemption_Shepher.png',
        benefits: '/images/tour-guide/tour_benefits.png',
    };

    // Mobile-only full-screen tour images (existing)
    window.__tourImgsMobile = {
        wallet: '/images/tour-guide/image_my_wallet.png',
        spending: '/images/tour-guide/image_my_spending_mobile.png',
        'travel-plus': '/images/tour-guide/image_10xplus_mobile.png',
        'travel-plus-free': '/images/tour-guide/image_10xplus_mobile.png',
        course: '/images/tour-guide/image_10x_course.png',
        points: '/images/tour-guide/image_my_points.png',
        nextcard: '/images/tour-guide/image_my_next_card.png',
        redemptions: '/images/tour-guide/image_redemption.png',
        benefits: '/images/tour-guide/tour_benefits.png',
    };

    window.launchUserTour = () => {
        if (isMobile()) openMobileTour();
        else {
            buildTour();
            startTour(0);
        }
    };

    window.launchTravelPlusStep = () => {
        launchTravelPlusOnlyStep();
    };

    // Watch for conditions to auto-launch the 10xTravel+ step
    const waitForSidebarAndLaunch = (retries = 10) => {
        const el = document.querySelector('.nav-10x-travel-plus');
        if (el) {
            // Scroll the sidebar to make the target visible
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Wait for scroll to complete before launching
            setTimeout(() => {
                launchTravelPlusOnlyStep();
            }, 400);
        } else if (retries > 0) {
            setTimeout(() => waitForSidebarAndLaunch(retries - 1), 300);
        }
    };

    watch(
        [shouldShowTravelPlusStep, activePopup],
        ([shouldShow, currentPopup]) => {
            if (
                shouldShow &&
                !currentPopup &&
                !travelPlusStepLaunched.value
            ) {
                travelPlusStepLaunched.value = true;
                // Wait for sidebar to render with retries
                waitForSidebarAndLaunch();
            }
        },
        { immediate: true }
    );

    onBeforeUnmount(() => {
        if (tour) {
            try {
                tour.off && tour.off('*');
            } catch (_) {}
            try {
                tour.cancel && tour.cancel();
            } catch (_) {}
            tour = null;
        }
    });
});

onBeforeUnmount(() => {
    try {
        window.removeEventListener('resize', computeShortScreen);
    } catch (_) {}
    try {
        window.removeEventListener('orientationchange', computeShortScreen);
    } catch (_) {}
});
</script>
